import type {
	ShadowFloor,
	ShadowLocation,
	ShadowSensor,
	ShadowZone,
} from "../../types/location-map";

import { EditFloorForm } from "../forms/edit-floor-form";
import { EditSensorForm } from "../forms/edit-sensor-form";
import { EditZoneForm } from "../forms/edit-zone-form";
import { LocationForm } from "../forms/location-form";

export type EditEntityModalProps = {
	floor?: null | ShadowFloor;
	location?: null | ShadowLocation;
	mapCenter?: [number, number] | null;
	onClose: () => void;
	/** Switch sensor edit target (URL) when another map pin is clicked. */
	onSelectSensor?: (sensorId: string) => void;
	sensor?: null | ShadowSensor;
	type: EditEntityType | null;
	zone?: null | ShadowZone;
};

/**
 * Which entity the consolidated edit modal should open.
 * Resolve the live documents from `locations` before rendering forms.
 */
export type EditEntityTarget =
	| { floorId: string; locationId: string; type: "floor" }
	| { locationId: string; sensorId: string; type: "sensor" }
	| { locationId: string; type: "location" }
	| { locationId: string; type: "zone"; zoneId: string };

export type EditEntityType = "floor" | "location" | "sensor" | "zone";

/**
 * Single edit entrypoint: mounts the matching entity form dialog by `type`.
 * Each form owns its own GeneralModal chrome.
 */
export const EditEntityModal = ({
	floor,
	location,
	mapCenter,
	onClose,
	onSelectSensor,
	sensor,
	type,
	zone,
}: EditEntityModalProps) => {
	switch (type) {
		case "floor":
			if (!floor) return null;
			return <EditFloorForm floor={floor} key={floor.id} onClose={onClose} />;
		case "location":
			if (!location) return null;
			return (
				<LocationForm
					key={location.id}
					location={location}
					mapCenter={mapCenter}
					mode="edit"
					onClose={onClose}
				/>
			);
		case null:
			return null;
		case "sensor":
			if (!location || !sensor) return null;
			return (
				<EditSensorForm
					location={location}
					mapCenter={mapCenter}
					onClose={onClose}
					onSelectSensor={onSelectSensor}
					sensor={sensor}
				/>
			);
		case "zone":
			if (!location || !zone) return null;
			return (
				<EditZoneForm
					key={zone.id}
					location={location}
					mapCenter={mapCenter}
					onClose={onClose}
					zone={zone}
				/>
			);
		default:
			return (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-primary/80 p-4"
					role="alert"
				>
					<div className="max-w-md rounded-lg border border-secondary bg-primary p-4 text-sm shadow-lg">
						<p className="font-medium text-primary">Cannot open edit dialog</p>
						<p className="mt-1 text-tertiary">
							Unexpected edit type “{String(type)}”. Expected location, floor,
							zone, or sensor.
						</p>
						<button
							className="text-brand mt-3 text-sm font-medium hover:underline"
							onClick={onClose}
							type="button"
						>
							Close
						</button>
					</div>
				</div>
			);
	}
};
